import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';
import type { Task, UserPublic } from '@underline/shared';
import { TaskCheckbox } from '@/components/shared/task-checkbox';
import { UserAvatar } from '@/components/users/user-avatar';
import { ease } from '@/lib/motion';
import { fmtDateBR, fmtTime, isOverdue } from '@/lib/dates';
import { cn } from '@/lib/utils';

interface TaskCardProps {
  task: Task;
  responsible?: UserPublic | undefined;
  onToggle: () => void;
  onClick?: (() => void) | undefined;
}

/* ── Meta inline (icone + texto mono, tom destrutivo opcional) ───────── */
function MetaInline({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone?: 'destructive' | undefined;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap font-mono text-[11px] uppercase tracking-wider',
        tone === 'destructive' ? 'text-destructive' : 'text-muted-foreground',
      )}
    >
      <Clock className="h-3 w-3" />
      {children}
    </span>
  );
}

/* ── TaskCard — card completo (grid de tarefas) ──────────────────────── */
export function TaskCard({ task, responsible, onToggle, onClick }: TaskCardProps) {
  const done = task.status === 'done';
  const overdue = !done && isOverdue(task.due_date, task.due_time);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.22, ease }}
      onClick={onClick}
      whileHover={done ? {} : { y: -4, boxShadow: 'var(--shadow-card)' }}
      whileTap={done ? {} : { scale: 0.99 }}
      className={cn(
        'flex cursor-pointer flex-col gap-3 rounded-2xl border bg-card p-[18px] shadow-elev',
        done ? 'opacity-70 border-border' : 'border-border',
        overdue && 'border-destructive/35',
      )}
    >
      {/* Topo: checkbox + bloco de conteudo */}
      <div className="flex items-start gap-3">
        <TaskCheckbox checked={done} onToggle={onToggle} />
        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex items-center gap-2">
            <span className="eyebrow">post</span>
            {done && (
              <span className="rounded-full bg-success/10 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-success">
                feito
              </span>
            )}
            {overdue && (
              <span className="rounded-full bg-destructive px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-destructive-foreground">
                atrasado
              </span>
            )}
          </div>
          <h3
            className={cn(
              'text-[15px] font-semibold leading-snug',
              done ? 'text-muted-foreground line-through' : 'text-foreground',
            )}
          >
            {task.title}
          </h3>
          {task.description && (
            <p className="mt-1 line-clamp-2 text-[13px] leading-snug text-muted-foreground">
              {task.description}
            </p>
          )}
        </div>
      </div>

      {/* Rodape: responsavel a esquerda, prazo a direita */}
      <div className="flex items-center justify-between border-t border-border pt-3">
        {responsible ? (
          <span className="inline-flex min-w-0 items-center gap-2">
            <UserAvatar
              name={responsible.name}
              colorHex={responsible.color_hex}
              url={responsible.avatar_url}
              size="sm"
            />
            <span className="truncate text-xs font-medium text-muted-foreground">
              {responsible.name.split(' ')[0]}
            </span>
          </span>
        ) : (
          <span />
        )}
        <MetaInline tone={overdue ? 'destructive' : undefined}>
          {fmtDateBR(task.due_date)}
          {task.due_time && ` · ${fmtTime(task.due_time)}`}
        </MetaInline>
      </div>
    </motion.div>
  );
}

/* ── TaskRow — linha compacta (dashboard "lista") ────────────────────── */
export function TaskRow({ task, responsible, onToggle, onClick }: TaskCardProps) {
  const done = task.status === 'done';
  const overdue = !done && isOverdue(task.due_date, task.due_time);

  return (
    <div
      onClick={onClick}
      className={cn(
        'flex cursor-pointer items-center gap-3 border-b border-border bg-card px-4 py-3',
        done && 'opacity-65',
      )}
    >
      <TaskCheckbox checked={done} onToggle={onToggle} size={20} />
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block truncate text-sm font-medium',
            done ? 'text-muted-foreground line-through' : 'text-foreground',
          )}
        >
          {task.title}
        </span>
        <span
          className={cn(
            'mt-0.5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider',
            overdue ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          <Clock className="h-[11px] w-[11px]" />
          {fmtDateBR(task.due_date)}
          {task.due_time && ` · ${fmtTime(task.due_time)}`}
        </span>
      </span>
      {responsible && (
        <UserAvatar
          name={responsible.name}
          colorHex={responsible.color_hex}
          url={responsible.avatar_url}
          size="sm"
        />
      )}
    </div>
  );
}
